import { socialApi } from "@bw/shared/api";
import {
  invalidateFeedQueries,
  scheduleFeedReconciliation,
} from "@bw/shared/hooks";
import type { FollowStatusResponse } from "@bw/shared/types/generated";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "../query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../guardian/denials";

/** One token-scoped directional follow relationship for a viewed player. */
export function useFollowPlayer(userId: string) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.social.followStatus(userId);
  const status = useQuery<FollowStatusResponse>({
    queryKey,
    queryFn: () => socialApi.getFollowStatus(userId),
    enabled: Boolean(userId),
  });
  const change = useMutation<void, unknown, boolean>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (nextFollowing) =>
      nextFollowing ? socialApi.follow(userId) : socialApi.unfollow(userId),
    onSuccess: (_result, nextFollowing) => {
      // This is server-confirmed state, not an optimistic relationship guess.
      queryClient.setQueryData<FollowStatusResponse>(queryKey, {
        is_following: nextFollowing,
      });
      invalidateFeedQueries(queryClient, [{ scope: "home" }]);
      // Follow projection is asynchronous; only Home audience membership needs
      // bounded reconciliation. This never turns into polling.
      scheduleFeedReconciliation(queryClient, [{ scope: "home" }]);
    },
  });

  return { status, change };
}
